import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { useIpBlacklist } from "../../context/IpBlacklistContext";
import { fetchWithAuth } from "../../hooks/useAuth";
import { Order } from "../../types/orders";
import { formatDate, formatPrice } from "../../utils/format";

interface CustomerStats {
  totalOrders: number;
  totalSpent: number;
  averageOrderValue: number;
  purchasedBooks: string[];
}

interface Customer {
  id: number;
  email: string;
  lastIp: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  orders: Order[];
  stats: CustomerStats;
}

export function CustomerDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { add: addIp } = useIpBlacklist();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
  });

  // Fetch customer details
  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/admin/customers/${id}`);
      if (!response.ok) throw new Error("Eroare la încărcarea clientului");
      const data = await response.json();
      setFormData({
        name: data.date.customer.name || "",
        email: data.date.customer.email,
      });
      return data.date.customer as Customer;
    },
  });

  // Update customer mutation
  const updateCustomer = useMutation({
    mutationFn: async (data: { name: string; email: string }) => {
      const response = await fetchWithAuth(`/api/admin/customers/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Eroare la actualizarea clientului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", id] });
      toast.success("Client actualizat cu succes");
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Sort orders by date (most recent first)
  const sortedOrders = React.useMemo(() => {
    if (!customer?.orders) return [];
    return [...customer.orders].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [customer?.orders]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">Se încarcă...</div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <h1 className="text-2xl font-bold mb-4">Clientul nu a fost găsit</h1>
        <Button onClick={() => navigate("/admin/customers")}>
          Înapoi la clienți
        </Button>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateCustomer.mutate(formData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          Client: {customer.name || customer.email}
        </h1>
        <Button onClick={() => navigate("/admin/customers")}>
          Înapoi la clienți
        </Button>
      </div>

      {/* Customer Details */}
      <Card>
        <CardHeader>
          <CardTitle>Detalii Client</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nume</label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Nume client"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="Email client"
                  type="email"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={updateCustomer.isPending}>
                  Salvează
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Anulează
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium mb-1">Nume</div>
                <div>{customer.name || "N/A"}</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Email</div>
                <div>{customer.email}</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">
                  Data Înregistrării
                </div>
                <div>{formatDate(customer.createdAt)}</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">
                  Ultima Actualizare
                </div>
                <div>{formatDate(customer.updatedAt)}</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Ultimul IP</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono">{customer.lastIp || "N/A"}</span>
                  {!!customer.lastIp && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-700 border-red-200 hover:bg-red-50"
                      onClick={async () => {
                        if (
                          !window.confirm(`Blochezi IP-ul ${customer.lastIp}?`)
                        ) {
                          return;
                        }
                        try {
                          await addIp(
                            customer.lastIp,
                            `Client #${customer.name} - ${customer.email}`
                          );
                          toast.success("IP adăugat în blacklist");
                        } catch (error) {
                          toast.error(
                            error instanceof Error
                              ? error.message
                              : "Eroare la adăugarea IP-ului în blacklist"
                          );
                        }
                      }}
                    >
                      Blochează IP
                    </Button>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <Button onClick={() => setIsEditing(true)}>Editează</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Statistici</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-500">
                Total Comenzi
              </div>
              <div className="text-2xl font-bold">
                {customer.stats.totalOrders}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">
                Total Cheltuit
              </div>
              <div className="text-2xl font-bold">
                {formatPrice(customer.stats.totalSpent)}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">
                Valoare Medie Comandă
              </div>
              <div className="text-2xl font-bold">
                {formatPrice(customer.stats.averageOrderValue)}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">
                Cărți Cumpărate
              </div>
              <div className="text-2xl font-bold">
                {customer.stats.purchasedBooks.length}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Order History */}
      <Card>
        <CardHeader>
          <CardTitle>Istoric Comenzi</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Total</TableHead>
                {/* <TableHead>Status</TableHead> */}
                <TableHead>Plată</TableHead>
                <TableHead>Livrare</TableHead>
                <TableHead>Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    Nu există comenzi
                  </TableCell>
                </TableRow>
              ) : (
                sortedOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>#{order.id}</TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>{formatPrice(order.totalPrice)}</TableCell>
                    {/* <TableCell>
                        <Badge>{order.shippingStatus}</Badge>
                      </TableCell> */}
                    <TableCell>
                      <Badge>{order.payment?.status || "N/A"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge>{order.shippingStatus}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        Detalii
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Purchased Books */}
      <Card>
        <CardHeader>
          <CardTitle>Cărți Cumpărate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {customer.stats.purchasedBooks.map((book, index) => (
              <Badge key={index} variant="secondary">
                {book}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
